<template>
  <div class="home single__service">
    <div v-if="!loading && dates && dates.length">
      <salutation :salutation="form.salutation" :surname="form.surname" />

      <div class="simple-service__description" v-if="$t('simpleService.description')">{{$t('simpleService.description')}}</div>

      <appointment-selection :validationRules="$v" :form="form" />

      <div class="simple-service__section">
        <div class="simple-service__title">{{$t('simpleService.service.title')}} *</div>

        <div class="simple-service__description" v-if="$t('simpleService.service.description')">{{$t('simpleService.service.description')}}</div>

        <service-selection-input :validationRules="$v" :options="services" v-model="selectedServices" :multiple="serviceViewConfigs.multiple" :display-prices="displayPrices" />
      </div>

      <div class="simple-service__section">
        <div class="simple-service__title">{{$t('simpleService.service.additionalServiceTitle')}}</div>

        <div class="simple-service__description" v-if="$t('simpleService.service.additionalServiceDescription')">{{$t('simpleService.service.additionalServiceDescription')}}</div>

        <service-selection-input :options="additionalServices" v-model="selectedAdditionalServices" :multiple="additionalServiceViewConfigs.multiple" :display-prices="displayPrices" />
      </div>

      <div class="simple-service__section">
        <div class="simple-service__title">{{$t('simpleService.customer.personalInfoTitle')}}</div>

        <div class="simple-service__description" v-if="$t('simpleService.customer.description')">{{$t('simpleService.customer.description')}}</div>

        <contact-information :form="form" :v="$v" />

        <div class="error-message network-error" v-if="requestError">{{$t('simpleService.errors.bookingError')}}</div>

        <agreements :form="form" :v="$v" class="car-agreements-modal" />
      </div>

      <div>
        <div class="simple-service__description" v-if="$t('simpleService.booking.bookingBtnDescription')">{{$t('simpleService.booking.bookingBtnDescription')}}</div>

        <button class="booking_btn car-btn car-btn-primary" id="booking" @click="createBooking()">{{$t('simpleService.booking.button.book')}}</button>
      </div>

      <div class="simple-service__location_details">
        <workshop-details />
      </div>
    </div>

    <div v-else-if="!loading && dates && !dates.length">
      <div v-if="!bookingNotAvailable" class="appointment__description--no-slots">{{ $t('simpleService.appointment.noAvailableDatesDescription') }}</div>

      <div v-else>
        <salutation :salutation="form.salutation" :surname="form.surname" />

        <div class="appointment__description--booking-not-available" v-html="bookingNotAvailable"></div>
      </div>

      <div class="simple-service__location_details">
        <workshop-details />
      </div>
    </div>

    <spinner class="simple-service__spinner" v-else />
  </div>
</template>

<script>
import ServiceSelectionInput from '@/modules/simpleService/components/ServiceSelectionInput';
import ContactInformation from '@/modules/simpleService/components/ContactInformation';
import AppointmentSelection from '@/modules/simpleService/components/AppointmentSelection';
import WorkshopDetails from '@/modules/simpleService/components/WorkshopDetails';
import { mapGetters } from 'vuex';
import Spinner from 'vue-simple-spinner';
import SMP_CONFIG from '@/util/smp_config';
import utils from '@/util/utils';
import { find, has } from 'lodash';
import Salutation from '@/modules/simpleService/components/Salutation';
import Agreements from '@/components/service-request/Agreements';

const { required, email } = require('vuelidate/lib/validators');

const validAgreements = (value) => {
  if (typeof value === 'undefined' || value === null || value === '') {
    return true
  }
  return value;
};

export default {
  name: 'SimpleServiceBooking',
  data: function () {
    return {
      loading: false,
      bookingNotAvailable: '',
      eCode: '01130086015',
      requestError: false,
      selectedModal: null,
      service: null,
      selectedServices: [],
      selectedAdditionalServices: [],
      serviceViewConfigs: {},
      additionalServiceViewConfigs: {},
      form: {
        selectedDate: null,
        selectedTimeSlot: null,
        salutation: this.$route.query.salutation ? this.$route.query.salutation : null,
        givenname: this.$route.query.firstname,
        surname: this.$route.query.lastname,
        plateNumber: this.$route.query.numberPlate,
        telNumber: this.$route.query.phone,
        email: this.$route.query.email,
        agreeTo: false
      }
    };
  },
  validations: {
    selectedServices: {
      required
    },
    form: {
      selectedDate: {
        required
      },
      selectedTimeSlot: {
        required
      },
      salutation: {
        required
      },
      givenname: {
        required
      },
      surname: {
        required
      },
      email: {
        required,
        email
      },
      plateNumber: {
        required
      },
      agreeTo: {
        validAgreements
      }
    }
  },
  methods: {
    createBooking: function () {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.loading = true;
      this.requestError = false;
      let selectedServices = this.selectedServices.map((s) => { return { serviceId: s.id } });
      let selectedAdditionalServices = this.selectedAdditionalServices.map((s) => { return { serviceId: s.id } });
      let payload = {
        form: this.form,
        serviceIds: [
          ...selectedServices.map(s => s.serviceId),
          ...selectedAdditionalServices.map(s => s.serviceId)
        ]
      };
      this.$store.dispatch('offer/createSimpleServiceBooking', payload).then(() => {
        this.$router.push({ name: 'bookingConfirmed' });
      }).catch(() => {
        this.requestError = true;
      }).finally(() => {
        this.loading = false;
      });
    }
  },
  components: {
    Salutation,
    AppointmentSelection,
    ServiceSelectionInput,
    ContactInformation,
    Spinner,
    WorkshopDetails,
    Agreements
  },
  computed: {
    ...mapGetters('capacity', ['dates']),
    ...mapGetters('serviceCase', ['agreements']),
    ...mapGetters('offer', {
      services: 'services',
      additionalServices: 'additionalServices'
    }),
    displayPrices() {
      return SMP_CONFIG.contextConfig.extended.featureConfig.servicePriceDisplay === 'visible';
    }
  },
  created() {
    let dmsCustomerCode = utils.getParameterByName('c');

    if (dmsCustomerCode) {
      this.$store.dispatch('location/getContactInfo', dmsCustomerCode)
        .then(res => { for (let [key, value] of Object.entries(res)) { if (value) this.form[key] = value } });
    }

    if (has(SMP_CONFIG, 'contextConfig.extended.featureConfig.bookingNotAvailable')) {
      this.bookingNotAvailable = SMP_CONFIG.contextConfig.extended.featureConfig.bookingNotAvailable;
      return;
    }

    this.loading = true;
    let capacityPromise = this.$store.dispatch('capacity/getCampaignCapacity');
    this.serviceViewConfigs = find(SMP_CONFIG.applicationConfig.serviceSelectionConfig.serviceViewConfigs, { name: 'MAINSERVICES' });
    this.additionalServiceViewConfigs = find(SMP_CONFIG.applicationConfig.serviceSelectionConfig.serviceViewConfigs, { name: 'ADDITIONALSERVICES' });
    let services = this.serviceViewConfigs.preSelectedServices2;
    let additionalServices = this.additionalServiceViewConfigs.preSelectedServices2;
    let agreementPromise = this.$store.dispatch('serviceCase/getAgreements');
    let getServicePromise = this.$store.dispatch('offer/getServices', { eCode: this.eCode, services, additionalServices });

    Promise.all([agreementPromise, getServicePromise, capacityPromise]).finally(() => {
      this.loading = false;
    });
  }
}
</script>
